import { memo, useCallback, useEffect, useState } from 'react';
import { Button, Select, Spin, TimePicker, message } from 'antd';
import {
  ClockCircleOutlined,
  CloseOutlined,
  GlobalOutlined,
  PlusOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

import {
  AvailabilityRule,
  DayOfWeek,
  getUserAvailability,
  setUserAvailability,
} from '../../api/availability';
import { availabilitySaveErrorMessage } from '../../utils/availabilityErrors';
import styles from './index.module.css';

interface Props {
  userId?: number;
}

const DAY_OPTIONS: { label: string; value: DayOfWeek }[] = [
  { label: 'Monday', value: 'MONDAY' },
  { label: 'Tuesday', value: 'TUESDAY' },
  { label: 'Wednesday', value: 'WEDNESDAY' },
  { label: 'Thursday', value: 'THURSDAY' },
  { label: 'Friday', value: 'FRIDAY' },
  { label: 'Saturday', value: 'SATURDAY' },
  { label: 'Sunday', value: 'SUNDAY' },
];

/** Three letters is enough to read as a day once it sits inside its own badge
 * - the full option list (used in the select's own dropdown) already spells
 * the name out, so this is purely the collapsed, always-visible label. */
const DAY_SHORT: Record<DayOfWeek, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

const SLOT_DURATION_OPTIONS = [15, 30, 45, 60];

// The backend validates this against java.time.ZoneId, so any IANA id it
// accepts works here too - this is just a reasonable, short starting list
// rather than every tz database entry.
const TIMEZONE_OPTIONS = Array.from(
  new Set([
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Amsterdam',
    'Asia/Kolkata',
  ]),
);

const emptyRule = (): AvailabilityRule => ({
  dayOfWeek: 'MONDAY',
  startTime: '09:00',
  endTime: '17:00',
  slotDurationMinutes: 30,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const timeToDayjs = (value: string): Dayjs => dayjs(value, 'HH:mm');
const dayjsToTime = (value: Dayjs): string => value.format('HH:mm');

/** Short timezone label for the collapsed badge - "New_York" from
 * "America/New_York", so the city reads at a glance without the region
 * prefix crowding a small pill. The select's own dropdown still shows the
 * full id, which is what actually gets saved. */
const timezoneShort = (tz: string): string => tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;

/**
 * Lets a hiring-team member set the recurring weekly hours candidates can
 * book an interview slot against (see CandidateBookingService /
 * AvailabilityRuleEntity on the backend). This is a separate model from the
 * "Calendar link" section above it on this page - that one is an outbound
 * link to an external scheduler (Calendly etc.), this one is Avrio's own
 * booking system with no external calendar involved.
 */
const Availability = ({ userId }: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [rules, setRules] = useState<AvailabilityRule[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setIsLoading(true);
    getUserAvailability(userId)
      .then((response) => {
        if (!cancelled) setRules(response.rules);
      })
      .catch(() => {
        if (!cancelled) {
          messageApi.open({
            type: 'error',
            content: 'Could not load your availability.',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // messageApi is stable across renders (antd's own guarantee); including
    // it would re-run this fetch on every render for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const updateRule = useCallback(
    (index: number, patch: Partial<AvailabilityRule>) => {
      setRules((prev) =>
        prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
      );
    },
    [],
  );

  const addRule = useCallback(() => {
    setRules((prev) => [...prev, emptyRule()]);
  }, []);

  const removeRule = useCallback((index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    if (!userId) return;
    // Both are always zero-padded HH:mm from the TimePicker, so a plain
    // string compare orders them correctly without a dayjs plugin.
    const invalid = rules.find((rule) => rule.endTime <= rule.startTime);
    if (invalid) {
      messageApi.open({
        type: 'error',
        content: 'Each rule’s end time must be after its start time.',
      });
      return;
    }
    setIsSaving(true);
    try {
      const response = await setUserAvailability(userId, rules);
      setRules(response.rules);
      messageApi.open({ type: 'success', content: 'Availability saved.' });
    } catch (error) {
      messageApi.open({
        type: 'error',
        content: availabilitySaveErrorMessage(error),
      });
    } finally {
      setIsSaving(false);
    }
  }, [userId, rules, messageApi]);

  if (isLoading) {
    return (
      <div className={`${styles.card} ${styles.loading}`}>
        <Spin tip="Loading..." />
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {contextHolder}
      <div className={styles.cardHeader}>
        <span className={styles.cardIcon} aria-hidden="true">
          <ScheduleOutlined />
        </span>
        <div>
          <h2 className={styles.cardTitle}>Interview availability</h2>
          <p className={styles.intro}>
            Set the recurring weekly hours candidates can book an interview slot
            with you. This is separate from the calendar link above — candidates
            book directly through Avrio, and a slot disappears from what they
            see as soon as someone books it.
          </p>
        </div>
      </div>

      {rules.length === 0 && (
        <div className={styles.empty}>
          <ClockCircleOutlined className={styles.emptyIcon} aria-hidden="true" />
          <p className={styles.emptyText}>
            No availability set yet. Add a day below to let candidates start
            booking with you.
          </p>
        </div>
      )}

      {rules.length > 0 && (
        <div className={styles.ruleList}>
          {rules.map((rule, index) => (
            <div key={index} className={styles.ruleRow}>
              <Select<DayOfWeek>
                className={styles.dayField}
                variant="borderless"
                popupMatchSelectWidth={false}
                options={DAY_OPTIONS}
                labelRender={() => <span className={styles.dayBadge}>{DAY_SHORT[rule.dayOfWeek]}</span>}
                value={rule.dayOfWeek}
                onChange={(value) => updateRule(index, { dayOfWeek: value })}
              />

              <div className={styles.ruleFields}>
                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>Hours</span>
                  <div className={styles.timeRange}>
                    <TimePicker
                      className={styles.timeField}
                      format="HH:mm"
                      minuteStep={5}
                      allowClear={false}
                      suffixIcon={null}
                      value={timeToDayjs(rule.startTime)}
                      onChange={(value) =>
                        value && updateRule(index, { startTime: dayjsToTime(value) })
                      }
                    />
                    <span className={styles.timeRangeSeparator}>–</span>
                    <TimePicker
                      className={styles.timeField}
                      format="HH:mm"
                      minuteStep={5}
                      allowClear={false}
                      suffixIcon={null}
                      value={timeToDayjs(rule.endTime)}
                      onChange={(value) =>
                        value && updateRule(index, { endTime: dayjsToTime(value) })
                      }
                    />
                  </div>
                </div>

                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>Slot length</span>
                  <Select
                    className={styles.slotField}
                    variant="borderless"
                    value={rule.slotDurationMinutes}
                    options={SLOT_DURATION_OPTIONS.map((minutes) => ({
                      label: `${minutes} min`,
                      value: minutes,
                    }))}
                    onChange={(value) => updateRule(index, { slotDurationMinutes: value })}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>
                    <GlobalOutlined /> Timezone
                  </span>
                  <Select
                    className={styles.timezoneField}
                    variant="borderless"
                    showSearch
                    popupMatchSelectWidth={false}
                    value={rule.timezone}
                    labelRender={() => timezoneShort(rule.timezone)}
                    options={TIMEZONE_OPTIONS.map((tz) => ({ label: tz, value: tz }))}
                    onChange={(value) => updateRule(index, { timezone: value })}
                  />
                </div>
              </div>

              <button
                type="button"
                className={styles.removeButton}
                aria-label={`Remove ${DAY_OPTIONS.find((d) => d.value === rule.dayOfWeek)?.label} availability`}
                onClick={() => removeRule(index)}
              >
                <CloseOutlined />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={styles.actions}>
        <Button icon={<PlusOutlined />} onClick={addRule}>
          Add day
        </Button>
        <Button type="primary" loading={isSaving} onClick={handleSave}>
          Save availability
        </Button>
      </div>
    </div>
  );
};

export default memo(Availability);
